import { memo } from 'react';

import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import cx from 'classnames';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';

import {
  Button,
  ComposedChart,
  Dropdown,
  DropdownItem,
  Icons,
  PendingContent,
  Spinner,
  Status,
  Tooltip,
} from 'plume-ui';
import { ComposedChartDataItem } from 'plume-ui/dist/utils/charts/chartTypes';

import { Routes, STANDARD_DATE_TIME_FORMAT } from '../../../config';
import { activePartnerAtom } from '../../../state';
import { AdjustableComponent } from '../../../utils/hooks/useClasses';
import { useRedirectToRoute } from '../../../utils/hooks/useRedirectToRoute';
import { makeTextExcerpt } from '../../../utils/testUtils';
import { getMonitorChartGroups } from '../helpers';

import { MonitorCellProps, MonitorCellStyles } from '../types';
import {
  mapOutreachStatusToColor,
  mapOutreachStatusToLabel,
} from '../components/MonitorInfo/helpers';
import { renderCustomTooltip } from '../components/MonitorCell/components/CellActionsMenu';
import CustomTooltip from '../components/MonitorCell/components/CustomTooltip';
import SignalIcon from '../../../assets/icons';
import { useVisibleInViewport } from 'plume-web-utils';
import FormattedMessage from '../../../utils/components/FormattedMessage';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);

interface FakeMonitorCellProps extends MonitorCellProps {
  fakeGraphData?: ComposedChartDataItem[];
  fakeLocationCount?: number;
  lastUpdateTime?: string;
  isLoading?: boolean;
}

const FakeMonitorCell: AdjustableComponent<
  FakeMonitorCellProps,
  MonitorCellStyles
> = ({
  name,
  supportOutreach,
  subscriberOutreach,
  supportWebhook,
  id,
  menuHandler,
  frequencyInHours = 0,
  fakeGraphData = [],
  fakeLocationCount = 0,
  lastUpdateTime,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const selectedPartner = useRecoilValue(activePartnerAtom);
  const redirectToRoute = useRedirectToRoute();
  const { ref, isInView } = useVisibleInViewport<HTMLDivElement>();

  const graphData = fakeGraphData;

  const formatGraphTicks = (value: any, index: number): string => {
    if (
      index === 0 ||
      index === graphData.length - 1 ||
      index === Math.floor(graphData.length / 2)
    )
      return `${value}`;
    return '';
  };

  menuHandler = {
    ...menuHandler,
    viewMonitorDetails: () => {
      redirectToRoute(Routes.AccessNetworkMonitorDetails, {
        partnerId: selectedPartner?.id,
        monitorId: id,
        eventId: 0,
      });
    },
  };

  const isNearRealTime = frequencyInHours === 0;
  const formattedUpdateTime = lastUpdateTime
    ? dayjs.utc(lastUpdateTime).local().format(STANDARD_DATE_TIME_FORMAT)
    : dayjs().format(STANDARD_DATE_TIME_FORMAT);

  return (
    <div
      ref={ref}
      className={cx('MonitorCell', {
        'MonitorCell--alarm': fakeLocationCount > 10000,
      })}
    >
      <div className="MonitorCell__header">
        <Tooltip
          type="hoverType"
          position="below"
          alignment="left"
          label={name}
          overrideMaxCharacters
          openInPortal
          classes={(current) => ({
            ...current,
            bubble: `${current.bubble} MonitorCell__titleTooltip`,
          })}
        >
          <Button
            styleVariant="tertiary"
            onClick={() =>
              redirectToRoute(Routes.AccessNetworkMonitorDetails, {
                partnerId: selectedPartner?.id,
                monitorId: id,
                eventId: 0,
              })
            }
            classes={(current) => ({
              ...current,
              root: `${current.root} MonitorCell__header`,
            })}
          >
            {makeTextExcerpt(name, 24)}
          </Button>
        </Tooltip>
      </div>
      <div className="MonitorCell__headerIcons">
        <Tooltip
          openInPortal
          label={
            isNearRealTime
              ? t('monitors.monitorCell.tooltips.nearRealTime')
              : t('monitors.monitorCell.tooltips.dataAsOf', {
                  date: formattedUpdateTime,
                  interpolation: { escapeValue: false },
                })
          }
        >
          <SignalIcon
            className="MonitorCell__lastUpdateIcon"
            width={24}
            height={24}
            name={isNearRealTime ? 'clockLightning' : 'clock'}
          />
        </Tooltip>
        <CellActionsMenu id={id || name} menuHandler={menuHandler} />
      </div>
      <div className="MonitorCell__metrics-container">
        <div className="MonitorCell__affected-locations">
          <div className="MonitorCell__label">
            <div className="MonitorCell__tooltipLabel">
              {t('monitors.monitorCell.currentAffected')}
              <Tooltip
                openInPortal
                label={t('monitors.monitorCell.tooltips.affectedLocations')}
              >
                <Icons.CircleInfoIcon width={14} height={14} />
              </Tooltip>
            </div>
            <div className="MonitorCell__affected-locations__count">
              {(fakeLocationCount || 0).toLocaleString('en-US')}
            </div>
          </div>
        </div>
        {subscriberOutreach !== undefined && (
          <div className="MonitorCell__outreach">
            <div className="MonitorCell__label">
              {t('monitors.monitorCell.subscriberOutreach')}
            </div>
            <span className="MonitorCell__outreach-status">
              <Status
                color={mapOutreachStatusToColor(subscriberOutreach.status)}
              />
              {mapOutreachStatusToLabel(subscriberOutreach.status)}
            </span>
          </div>
        )}
        {supportOutreach !== undefined && (
          <div className="MonitorCell__outreach">
            <div className="MonitorCell__label">
              {t('monitors.monitorCell.supportOutreach')}
            </div>
            <span className="MonitorCell__outreach-status">
              <Status
                color={mapOutreachStatusToColor(supportOutreach.status)}
              />
              {mapOutreachStatusToLabel(supportOutreach.status)}
            </span>
          </div>
        )}
        {supportWebhook !== undefined && (
          <div className="MonitorCell__outreach">
            <div className="MonitorCell__label">
              {t('monitors.monitorCell.supportWebhook')}
            </div>
            <span className="MonitorCell__outreach-status">
              <Status color={mapOutreachStatusToColor(supportWebhook.status)} />
              {mapOutreachStatusToLabel(supportWebhook.status)}
            </span>
          </div>
        )}
      </div>
      <div className="MonitorCell__chart-container">
        <PendingContent loading={isLoading} loader={Spinner} transparent>
          {!!graphData.length && isInView && (
            <ComposedChart
              curveType={'monotoneX'}
              data={graphData}
              groups={getMonitorChartGroups(1)}
              config={{
                showPercentageOnTooltip: false,
                xAxisLabelAngle: 0,
                showTooltipTitleForNumerical: true,
                showVerticalCartesianGrid: true,
                minHeight: 'auto',
                xAxisPadding: { left: 20, right: 20 },
                xAxisHeightOverride: 16,
                allowDecimalsY: false,
              }}
              stacked
              showDots={false}
              tickFormatter={formatGraphTicks}
              customTooltip={
                <CustomTooltip
                  data={graphData}
                  dataIdentifier="name"
                  renderTooltipContent={renderCustomTooltip}
                />
              }
            />
          )}
        </PendingContent>
      </div>
    </div>
  );
};

export default memo(FakeMonitorCell);

const CellActionsMenu = ({
  id,
  menuHandler = {},
}: {
  id: string;
  menuHandler?: Record<string, (id?: string) => void>;
}) => {
  const noActionDefined = (cmd: string, id: string) => {
    console.warn('Cannot find menu handler for : ', cmd, id);
  };

  return (
    <Dropdown
      listPosition="right"
      closeOnItemClick
      button={<Icons.DotsVerticalIcon width={24} />}
    >
      <DropdownItem
        onClick={() =>
          menuHandler.viewMonitorDetails !== undefined
            ? menuHandler.viewMonitorDetails(id)
            : noActionDefined('viewDetailPage', id)
        }
      >
        <FormattedMessage id="monitors.monitorActions.viewDetailPage" />
      </DropdownItem>
    </Dropdown>
  );
};
